import React, { useEffect, useMemo, useState } from 'react'
import { FaRegTrashAlt } from 'react-icons/fa'
import { FaCalendarDays } from 'react-icons/fa6'
import { FiRefreshCw, FiSearch } from 'react-icons/fi'
import { IoMdCheckmarkCircleOutline } from 'react-icons/io'
import { BsShieldExclamation } from 'react-icons/bs'
import CustomEmailDialog from '../../components/forms/custom-email-dialog.components'
import AdmiSubHeader from '../../components/subheader/admi-subheader.components'
import { useOrder } from '../../hooks/useAuth'
import { infoToast } from '../../utils/firebase/firebase.utils'
import { AdmiMenu } from './admi-menu.components'
import './admi.styles.scss'
import './orders.styles.scss'

const PAGE_SIZE = 8

const TABLE_HEAD = ['Customer', 'Status', 'Event details', 'Cake details']

const formatDateTime = (value) => {
  if (!value) return 'Unknown date'
  const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value)
  if (Number.isNaN(date.getTime())) return 'Unknown date'
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const formatEventDate = (value) => {
  if (!value) return 'No event date'
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number)
    const date = new Date(y, m - 1, d)
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }
  const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const OrdersView = () => {
  const { orders, getOrders, updateOrder } = useOrder()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [refreshing, setRefreshing] = useState(false)
  const [busyId, setBusyId] = useState(null)

  useEffect(() => {
    if (typeof getOrders === 'function') getOrders()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setPage(1)
  }, [search, filter])

  const refreshOrders = async () => {
    if (typeof getOrders !== 'function' || refreshing) return
    setSearch('')
    setFilter('all')
    setPage(1)
    setRefreshing(true)
    try {
      await getOrders()
    } finally {
      setRefreshing(false)
    }
  }

  const filtered = useMemo(() => {
    const list = Array.isArray(orders) ? orders.filter((item) => item?.del !== 'yes') : []
    const q = search.trim().toLowerCase()

    return list.filter((item) => {
      const isOpen = item.status !== 'yes'
      if (filter === 'open' && !isOpen) return false
      if (filter === 'closed' && isOpen) return false

      if (!q) return true
      const cakes = Array.isArray(item.cake_list)
        ? item.cake_list
            .map((c) => `${c.cake_type || ''} ${c.flavor || ''} ${c.dietary_rests || ''} ${c.specific_dets || ''}`)
            .join(' ')
        : ''
      const haystack =
        `${item.fullname || ''} ${item.email || ''} ${item.phone || ''} ${item.delivery_add || ''} ${cakes}`.toLowerCase()
      return haystack.includes(q)
    })
  }, [orders, search, filter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  const counts = useMemo(() => {
    const list = Array.isArray(orders) ? orders.filter((item) => item?.del !== 'yes') : []
    return {
      all: list.length,
      open: list.filter((item) => item.status !== 'yes').length,
      closed: list.filter((item) => item.status === 'yes').length,
    }
  }, [orders])

  const setOrderStatus = async (item, nextStatus) => {
    if (!item?.id) {
      return infoToast('This order is missing an id and cannot be updated.')
    }

    const closing = nextStatus === 'yes'
    const label = item.fullname || item.email || 'this customer'
    const ok = window.confirm(
      closing ? `Close ${label}'s order?` : `Reopen ${label}'s order?`
    )
    if (!ok) return

    setBusyId(item.id)
    try {
      await updateOrder(
        {
          ...item,
          status: nextStatus,
        },
        closing ? 'Order closed.' : 'Order reopened.'
      )
    } catch (error) {
      console.error('Update order failed:', error)
      infoToast('Could not update order. Please try again.')
    } finally {
      setBusyId(null)
    }
  }

  const deleteOrder = async (item) => {
    if (!item?.id) {
      return infoToast('This order is missing an id and cannot be deleted.')
    }

    const ok = window.confirm(`Delete order from ${item.fullname || item.email || 'this customer'}?`)
    if (!ok) return

    setBusyId(item.id)
    try {
      await updateOrder(
        {
          ...item,
          del: 'yes',
        },
        'Order deleted.'
      )
    } catch (error) {
      console.error('Delete order failed:', error)
      infoToast('Could not delete order. Please try again.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="admin-shell">
      <AdmiSubHeader h2="Orders" h6="Review booking requests and update status" />
      <AdmiMenu />

      <div className="admin-shell__body orders-v2">
        <div className="admin-toolbar">
          <div className="admin-toolbar__search">
            <FiSearch size={15} aria-hidden="true" />
            <label className="admin-toolbar__search-field">
              <span className="sr-only">Search orders</span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search name, phone, email, or cake"
              />
            </label>
            <button
              type="button"
              className={`admin-toolbar__refresh${refreshing ? ' is-spinning' : ''}`}
              onClick={refreshOrders}
              disabled={refreshing}
              aria-label="Clear filters and refresh orders"
              title="Clear filters and refresh"
            >
              <FiRefreshCw size={15} aria-hidden="true" />
            </button>
          </div>

          <div className="admin-toolbar__actions">
            <div className="admin-toolbar__filters" role="group" aria-label="Filter orders">
              {[
                { id: 'all', label: `All (${counts.all})` },
                { id: 'open', label: `Open (${counts.open})` },
                { id: 'closed', label: `Closed (${counts.closed})` },
              ].map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={filter === item.id ? 'is-active' : ''}
                  onClick={() => setFilter(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <CustomEmailDialog />
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="orders-v2__empty">
            {counts.all === 0
              ? 'No orders yet. New Book form requests will show up here.'
              : 'No orders match this search or filter.'}
          </p>
        ) : (
          <>
            <p className="orders-v2__page-meta">
              Showing {(currentPage - 1) * PAGE_SIZE + 1}
              {'–'}
              {Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
            </p>

            <div className="orders-v2__table-wrap">
              <table className="orders-v2__table">
                <thead>
                  <tr>
                    {TABLE_HEAD.map((head) => (
                      <th key={head}>{head}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((item, index) => {
                    const isOpen = item.status !== 'yes'
                    const cakes = Array.isArray(item.cake_list) ? item.cake_list : []
                    const isDelivery = item.delivery === 'yes'
                    const isLast = index === pageItems.length - 1

                    return (
                      <tr key={item.id || `${item.email}-${item.created_at}`} className={isLast ? 'is-last' : ''}>
                        <td>
                          <div className="orders-v2__customer">
                            <span className="orders-v2__avatar" aria-hidden="true">
                              <FaCalendarDays size={14} />
                            </span>
                            <div>
                              <p className="orders-v2__name">{item.fullname || 'Customer'}</p>
                              {item.email ? (
                                <a href={`mailto:${item.email}`} className="orders-v2__link">
                                  {item.email}
                                </a>
                              ) : null}
                              {item.phone ? (
                                <a href={`tel:${item.phone}`} className="orders-v2__link orders-v2__phone">
                                  {item.phone}
                                </a>
                              ) : null}
                              <p className="orders-v2__requested">{formatDateTime(item.created_at)}</p>
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className="orders-v2__status-cell">
                            <span
                              className={`orders-v2__chip${isOpen ? ' is-open' : ' is-closed'}`}
                            >
                              {isOpen ? (
                                <BsShieldExclamation size={13} aria-hidden="true" />
                              ) : (
                                <IoMdCheckmarkCircleOutline size={14} aria-hidden="true" />
                              )}
                              {isOpen ? 'Open' : 'Closed'}
                            </span>

                            {isOpen ? (
                              <button
                                type="button"
                                className="orders-v2__row-action"
                                onClick={() => setOrderStatus(item, 'yes')}
                                disabled={busyId === item.id}
                              >
                                {busyId === item.id ? 'Updating…' : 'Close order'}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="orders-v2__row-action is-reopen"
                                onClick={() => setOrderStatus(item, 'no')}
                                disabled={busyId === item.id}
                              >
                                {busyId === item.id ? 'Updating…' : 'Reopen'}
                              </button>
                            )}

                            <button
                              type="button"
                              className="orders-v2__row-delete"
                              onClick={() => deleteOrder(item)}
                              disabled={busyId === item.id}
                            >
                              <FaRegTrashAlt size={12} aria-hidden="true" />
                              Delete
                            </button>
                          </div>
                        </td>

                        <td>
                          <div className="orders-v2__event">
                            <p className="orders-v2__event-date">{formatEventDate(item.evt_date)}</p>
                            {isDelivery ? (
                              <>
                                <p>Delivery{item.evt_time ? ` @ ${item.evt_time}` : ''}</p>
                                {item.delivery_add ? (
                                  <p className="orders-v2__address">{item.delivery_add}</p>
                                ) : null}
                                {item.special_inst ? (
                                  <>
                                    <p className="orders-v2__mini-label">Instructions</p>
                                    <p className="orders-v2__instructions">{item.special_inst}</p>
                                  </>
                                ) : null}
                              </>
                            ) : (
                              <p>Pickup{item.evt_time ? ` @ ${item.evt_time}` : ''}</p>
                            )}
                          </div>
                        </td>

                        <td>
                          <div className="orders-v2__cakes-col">
                            {cakes.length === 0 ? (
                              <p className="orders-v2__muted">No cakes listed</p>
                            ) : (
                              cakes.map((cake, cakeIndex) => (
                                <div
                                  key={cake.c || `${item.id}-cake-${cakeIndex}`}
                                  className="orders-v2__cake-row"
                                >
                                  <p className="orders-v2__cake-title">
                                    {cake.cake_type || 'Cake'}
                                    {cake.qty ? ` (${cake.qty})` : ''}
                                  </p>
                                  {cake.flavor ? <p>Flavor: {cake.flavor}</p> : null}
                                  {cake.dietary_rests ? <p>{cake.dietary_rests}</p> : null}
                                  {cake.specific_dets ? <p>{cake.specific_dets}</p> : null}
                                </div>
                              ))
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 ? (
              <div className="orders-v2__pager">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                >
                  Previous
                </button>
                <span>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                >
                  Next
                </button>
              </div>
            ) : null}
          </>
        )}

        <p className="orders-v2__note">
          Closing an order marks it done in Firestore. Delete hides it from this list (soft delete).
          There is no payment step yet; these are booking requests from the Book page.
        </p>
      </div>
    </div>
  )
}

export default OrdersView
